import { createContext } from "react";
import type { QueryObserverResult } from "@tanstack/react-query";

import type { Location, LocationOption } from "@/views/production/saved-jobs/types/types";
import type { UserFavoriteDesigns } from "@/views/production/saved-jobs/hooks/use-user-favorite-designs";
import type { Flute, SavedJobPackagingDesign } from "@/views/production/saved-jobs/types/dependencies";
import type {
	CorrugateBrand,
	DesignParameter,
	PackagingComponentCartonSaved,
	PackagingComponentLabelSaved,
	SavedPackagingSolution,
} from "@/schemas";

export interface SavedJobDependencyContextValue {
	locationOptions: LocationOption[];
	location: Location;
	packStationKey?: string;
	setPackStationKey: (key: string | undefined) => void;
	setLocation: (location: Location) => void;
	flutes: Flute[];
	brands: CorrugateBrand[];
	packagingDesigns: SavedJobPackagingDesign[];
	lookupDesignName: (designId: number) => string;
	lookupDesignXValues: (designId: number) => DesignParameter[];
	sendToQueue: (solutionToSend: SavedPackagingSolution, quantity: string) => Promise<void>;
	sendToQueuev2: (solutionToSend: SavedPackagingSolution) => Promise<void>;
	sendSegmentedCartonSolution: (
		carton: PackagingComponentCartonSaved,
		solutionAlias: string,
		isPrimary?: boolean,
	) => Promise<void>;
	sendSegmentedLabelSolution: (label: PackagingComponentLabelSaved) => Promise<void>;
	isLoadingDependencies: boolean;
	refetchDependencies: () => void;
	errorDependencies: Error | null | undefined;
	userFavoriteDesigns: Record<string, boolean>;
	refetchUserFavoriteDesigns: () => Promise<QueryObserverResult<UserFavoriteDesigns, Error>>;
}

export const SavedJobDependencyContext = createContext<SavedJobDependencyContextValue>({
	locationOptions: [],
	location: {},
	packStationKey: undefined,
	setPackStationKey: () => {},
	setLocation: () => {},
	flutes: [],
	userFavoriteDesigns: {},
	refetchUserFavoriteDesigns: async () => null!,
	brands: [],
	packagingDesigns: [],
	lookupDesignName: (designId) => String(designId),
	lookupDesignXValues: () => [],
	sendToQueue: async () => {},
	sendToQueuev2: async () => {},
	sendSegmentedCartonSolution: async () => {},
	sendSegmentedLabelSolution: async () => {},
	isLoadingDependencies: true,
	refetchDependencies: () => {},
	errorDependencies: undefined,
});
